<template>
  <div>
    <label class="flex cursor-pointer items-start gap-3">
      <input
        :checked="modelValue"
        type="checkbox"
        class="mt-0.5 size-4 shrink-0 rounded-sm text-brand"
        :aria-invalid="!!error"
        :aria-describedby="error ? errorId : undefined"
        @change="
          emit('update:modelValue', ($event.target as HTMLInputElement).checked)
        "
      />
      <i18n-t
        keypath="checkout/step/create/terms/label"
        tag="span"
        class="text-sm text-rhino"
      >
        <template #tos>
          <a
            :href="termsUrl"
            target="_blank"
            rel="noopener"
            class="font-medium text-brand underline hover:no-underline"
            >{{ t('meta/terms_of_use') }}</a
          >
        </template>
        <template #privacy>
          <a
            :href="privacyUrl"
            target="_blank"
            rel="noopener"
            class="font-medium text-brand underline hover:no-underline"
            >{{ t('meta/privacy_policy') }}</a
          >
        </template>
      </i18n-t>
    </label>
    <p v-if="error" :id="errorId" class="mt-1.5 text-sm text-danger-fg">
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { getLegacyLocalePrefix } from '~/helpers/legacyLocale';

defineProps<{
  /** Whether the consent checkbox is ticked (v-model). */
  modelValue: boolean;
  /** Inline validation message shown below the checkbox when set. */
  error?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [boolean];
}>();

const { t, locale } = useI18n();
const currencyStore = useCurrencyStore();

// Stable, SSR-safe id linking the checkbox to its validation message via
// aria-describedby (two instances can be mounted at once — saved-method + the
// chargebee form slot — so a static id would collide).
const errorId = useId();

// Terms & Privacy links, built exactly like the footer (ThePageFooter →
// footer/features/index.ts): a plain locale/currency-prefixed path to the
// legacy legal pages, opened in a new tab. Deliberately NOT
// useWebinterfaceUrl('/terms') — for an authenticated user (checkout always is)
// that helper returns an auth/setLocale redirect, which is wrong for a legal
// link that should open the page directly in a new tab.
const termsUrl = computed(
  () => `/${getLegacyLocalePrefix(locale.value, currencyStore.currency)}/terms`,
);
const privacyUrl = computed(
  () =>
    `/${getLegacyLocalePrefix(locale.value, currencyStore.currency)}/privacy`,
);
</script>
